import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Badge,
  Card,
  CardBody,
  Container,
} from "reactstrap";
import { ChevronDown, ChevronRight } from "react-feather";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { baseURL } from "../../../../Services/api/baseURL";

const apiBase = `${baseURL}/api`;

const authHeader = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  return { Authorization: token };
};

const roleSchema = Yup.object({
  role_name: Yup.string().trim().min(2).max(80).required("Role name is required"),
  code: Yup.string()
    .trim()
    .matches(/^[a-z][a-z0-9_]*$/, "lowercase letters, numbers, underscores; starts with a letter")
    .min(2)
    .max(60)
    .nullable(),
  description: Yup.string().max(500).nullable(),
  permissions: Yup.array().of(Yup.string()),
});

const RoleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [expandedModules, setExpandedModules] = useState({});
  const [loadingRole, setLoadingRole] = useState(isEdit);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const formik = useFormik({
    initialValues: { role_name: "", code: "", description: "", permissions: [] },
    validationSchema: roleSchema,
    onSubmit: async (values) => {
      try {
        if (isEdit) {
          const patch = {
            role_name: values.role_name,
            description: values.description || "",
          };
          if (!isSuperAdmin) patch.permissions = values.permissions || [];
          await axios.patch(`${apiBase}/roles/${id}`, patch, { headers: authHeader() });
          Swal.fire({ icon: "success", title: "Role updated", timer: 1500, showConfirmButton: false });
        } else {
          await axios.post(
            `${apiBase}/roles`,
            {
              role_name: values.role_name,
              code: values.code ? values.code.trim() : undefined,
              description: values.description || "",
              permissions: values.permissions || [],
            },
            { headers: authHeader() }
          );
          Swal.fire({ icon: "success", title: "Role created", timer: 1500, showConfirmButton: false });
        }
        navigate("/role-management");
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not save",
          text: err?.response?.data?.message || err.message,
        });
      }
    },
  });

  const loadPermissions = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/permissions`, { headers: authHeader() });
      const grouped = res.data?.data?.grouped || {};
      setGroupedPermissions(grouped);
      setExpandedModules(
        Object.keys(grouped).reduce((acc, m) => ({ ...acc, [m]: true }), {})
      );
    } catch (err) {
      console.warn("Failed to load permissions", err?.response?.data || err.message);
    }
  }, []);

  const loadRole = useCallback(async () => {
    if (!id) return;
    setLoadingRole(true);
    try {
      const res = await axios.get(`${apiBase}/roles/${id}`, { headers: authHeader() });
      const role = res.data?.data || res.data;
      setIsSuperAdmin(role.code === "super_admin");
      formik.resetForm({
        values: {
          role_name: role.role_name || "",
          code: role.code || "",
          description: role.description || "",
          permissions: Array.isArray(role.permissions) ? [...role.permissions] : [],
        },
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed to load role", text: err?.response?.data?.message || err.message });
      navigate("/role-management");
    } finally {
      setLoadingRole(false);
    }
  }, [id]);

  useEffect(() => { loadPermissions(); }, [loadPermissions]);
  useEffect(() => { loadRole(); }, [loadRole]);

  const toggleModule = (module) =>
    setExpandedModules((prev) => ({ ...prev, [module]: !prev[module] }));

  const togglePermission = (code) => {
    const current = formik.values.permissions || [];
    const next = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code];
    formik.setFieldValue("permissions", next);
  };

  const toggleModuleAll = (module) => {
    const codes = (groupedPermissions[module] || []).map((p) => p.code);
    const current = new Set(formik.values.permissions || []);
    const allSelected = codes.every((c) => current.has(c));
    const next = allSelected
      ? [...current].filter((c) => !codes.includes(c))
      : [...new Set([...current, ...codes])];
    formik.setFieldValue("permissions", next);
  };

  const selectedSet = useMemo(
    () => new Set(formik.values.permissions || []),
    [formik.values.permissions]
  );
  const moduleNames = Object.keys(groupedPermissions);

  if (loadingRole) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  return (

    <Fragment>
      <Container fluid={true} className="theme-form product-form">
        <div className="d-flex align-items-center my-4 gap-3 page-title">
          {/* <Button color="link" className="p-0 text-dark" onClick={() => navigate("/role-management")}>
            <ArrowLeft size={20} />
          </Button> */}
          <h4 className="">{isEdit ? `Edit Role — ${formik.values.role_name}` : "Add Role"}</h4>
        </div>
      </Container>

      <Card>
        <CardBody>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Role name *</Label>
                  <Input
                    name="role_name"
                    value={formik.values.role_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={!!(formik.touched.role_name && formik.errors.role_name)}
                    placeholder="e.g. Store Manager"
                  />
                  {formik.touched.role_name && formik.errors.role_name && (
                    <small className="text-danger">{formik.errors.role_name}</small>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>
                    Code{" "}
                    {isEdit && <small className="text-muted">(cannot be changed)</small>}
                  </Label>
                  <Input
                    name="code"
                    value={formik.values.code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="e.g. warehouse_lead"
                    disabled={isEdit}
                    invalid={!!(formik.touched.code && formik.errors.code)}
                  />
                  {formik.touched.code && formik.errors.code && (
                    <small className="text-danger">{formik.errors.code}</small>
                  )}
                  <small className="text-muted d-block mt-1">
                    Optional stable identifier used by route guards. Leave blank to auto-generate later.
                  </small>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Description</Label>
              <Input
                type="textarea"
                name="description"
                rows={2}
                value={formik.values.description}
                onChange={formik.handleChange}
                placeholder="What this role is for…"
              />
            </FormGroup>

            {isSuperAdmin ? (
              <div className="alert alert-info">
                The <strong>super_admin</strong> role always holds every permission. Its
                permission list cannot be edited here.
              </div>
            ) : (
              <Fragment>
                <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                  <Label className="mb-0">
                    Permissions ({selectedSet.size} selected)
                  </Label>
                  <div className="d-flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      color="link"
                      onClick={() => setExpandedModules(
                        Object.keys(groupedPermissions).reduce((a, m) => ({ ...a, [m]: true }), {})
                      )}
                    >
                      Expand all
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      color="link"
                      onClick={() => setExpandedModules(
                        Object.keys(groupedPermissions).reduce((a, m) => ({ ...a, [m]: false }), {})
                      )}
                    >
                      Collapse all
                    </Button>
                  </div>
                </div>

                <div style={{ border: "1px solid #eee", borderRadius: 6, padding: 12 }}>
                  {moduleNames.length === 0 && (
                    <small className="text-muted">Loading permissions…</small>
                  )}
                  {moduleNames.map((module) => {
                    const perms = groupedPermissions[module] || [];
                    const allSelected = perms.length > 0 && perms.every((p) => selectedSet.has(p.code));
                    const someSelected = perms.some((p) => selectedSet.has(p.code));
                    const expanded = !!expandedModules[module];

                    return (
                      <div key={module} className="mb-2 border-bottom pb-2">
                        <div
                          className="d-flex align-items-center justify-content-between py-1"
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleModule(module)}
                        >
                          <div className="d-flex align-items-center">
                            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            <strong className="ms-1 text-capitalize">
                              {module.replace(/_/g, " ")}
                            </strong>
                            <Badge
                              color={allSelected ? "success" : someSelected ? "warning" : "secondary"}
                              className="ms-2"
                            >
                              {perms.filter((p) => selectedSet.has(p.code)).length}/{perms.length}
                            </Badge>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            color="link"
                            onClick={(e) => { e.stopPropagation(); toggleModuleAll(module); }}
                          >
                            {allSelected ? "Clear" : "Select all"}
                          </Button>
                        </div>

                        {expanded && (
                          <Row className="mt-1">
                            {perms.map((p) => {
                              const checked = selectedSet.has(p.code);
                              return (
                                <Col md="4" key={p.code}>
                                  <div
                                    className="d-flex align-items-start gap-2 mb-2"
                                    style={{ cursor: "pointer", userSelect: "none" }}
                                    onClick={() => togglePermission(p.code)}
                                  >
                                    <div
                                      style={{
                                        flexShrink: 0,
                                        marginTop: 2,
                                        width: 16,
                                        height: 16,
                                        borderRadius: 3,
                                        border: `2px solid ${checked ? "#d3178a" : "#ccc"}`,
                                        backgroundColor: checked ? "#d3178a" : "white",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {checked && (
                                        <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                          <path d="M1 3.5L3 5.5L8 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      )}
                                    </div>
                                    <div>
                                      <span className="text-capitalize" style={{ fontSize: 13 }}>
                                        {p.action.replace(/_/g, " ")}
                                      </span>
                                      {p.description && (
                                        <small className="text-muted d-block" style={{ fontSize: "11px" }}>
                                          {p.description}
                                        </small>
                                      )}
                                    </div>
                                  </div>
                                </Col>
                              );
                            })}
                          </Row>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Fragment>
            )}

            <div className="text-end gap-2 mt-4">
              <Button
                type="button"
                className="cancel_Btn me-2"
                onClick={() => navigate("/role-management")}
              >
                Cancel
              </Button>
              <Button type="submit"  disabled={formik.isSubmitting}>
                {formik.isSubmitting
                  ? "Saving…"
                  : isEdit
                    ? "Save changes"
                    : "Create role"}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default RoleForm;
